import { alpha, Box, Button, Divider, ListSubheader, Menu, MenuItem, styled, Tooltip } from '@mui/material'
import { OpenInNew } from '@mui/icons-material'
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { generatePath, useLocation } from 'react-router-dom'
import { AppContext } from '../../../App'
import { INITIAL_CANDLE_COUNT, MULTIPANE_FULLSCREEN_MODE_ID } from '../../../assets/constants'
import SplitHorizontalIcon from '../../../assets/icons/SplitHorizontalIcon'
import SplitVerticalIcon from '../../../assets/icons/SplitVerticalIcon'
import { useDispatch, useSelector } from 'react-redux'
import { selectActiveWidget, selectLayout, selectLayoutWidgets } from '../../../selectors/widgetDashboard'
import { selectActiveLayout, selectActiveLayoutId } from '../../../selectors/home'
import { GLOBAL_WIDGET_OPTION_MENU_ITEMS, MULTI_STRIKE_WIDGET_OPTION_MENU_ITEMS, WidgetOptionMenuItem, WidgetTypes } from '../../widgetDashboard/widgetDashboard.enums'
import { darkBg, white } from '../../../assets/colors'
import { darkHoverBtnColor, lightHoverBtnColor } from '../../../theme/theme'
import { deleteWidget, updateWidgetType, updateWidgetSettings } from '../../../reducer/widgetDashboard.reducer'
import { updateMultiStrikeChartConfig } from '../../../reducer/chart.reducer'
import { selectMultiStrikeChartConfigChartType, selectMultiStrikeChartConfigCumOi, selectMultiStrikeChartConfigShowChange } from '../../../selectors/chart'
import { getDefaultMultiStrikeChartSettings } from '../../../reducer/multiStrikeChart.reducer'
import {
    getMultiStrikeChartTypeMenuValue,
    isOiValueChartType,
    resolveMultiStrikeChartTypeSelection,
} from '../../multiStikeChart/multistrikeChartTypeHelpers'
import { useUser } from '../../../context/UserContext'
import { FeatureCategory, FeatureSubCategory } from '../../adminUserRestrictions/Restrictions.constants'
import { Group, setFeatureRestrictionDialog } from '../../../reducer/user.reducer'
import { NonInteractiveTooltip } from '../../custom'
import BinIcon from '../../../assets/icons/BinIcon'
import ResetIcon from '../../../assets/icons/ResetIcon'
import ThinArrowLeftIcon from '../../../assets/icons/ThinArrowLeftIcon'
import ThinArrowRightIcon from '../../../assets/icons/ThinArrowRightIcon'
import AddIcon from '../../../assets/icons/AddIcon'
import SubtractIcon from '../../../assets/icons/SubtractIcon'
import Widget from '../../../assets/icons/widget'
import FullScreenIcon from '../../../assets/icons/FullScreenIcon'
import { FullScreenToggle, selectMultiPaneFullScreen, selectWidgetPaneId } from '../../../selectors/common'
import Chart from '../chart'
import { ChartEvents } from '../chart.events'
import { IChartApi } from 'lightweight-charts'
import { ShortcutsCategories, ShortcutType } from '../../KeyboardShortcuts/Shortcuts.types'
import { ChartSettingsShortcutsKeyName } from '../../KeyboardShortcuts/ShortcutsKeymap'
import useShortcuts from '../../../hooks/useShortcuts'
import { setWidgetsPaneId, toggleFullscreen } from '../../../actions/common.actions'
import FullScreenReverseIcon from '../../../assets/icons/FullScreenReverseIcon'
import { Security, SecurityCategory_CategoryCode } from '../../../proto/generated/security'
import { useResizeDetector } from 'react-resize-detector'
import { ActionType, useUndoRedoContext } from '../../UndoRedoFunctonalities/UndoRedo.context'
import { WidgetDashboardConfig } from '../../widgetDashboard/widgetDashboard.interface'
import { countVisiblePanes } from '../../multiPane/multipPaneLayout.helper'
import AnalyticsManager from '../../../analytics/AnalyticsManager'
import { getSecurityAsync, getSecurityKey, isFnOSecurity } from '../../../util/security'
import { selectPrimarySecurity } from '../../../selectors/home'
import { isExpressionMode } from '../../../util/expression'
import { isEmbeddedModeSet } from '../../../util/general'
import ROUTES from '../../../assets/routes'
import { exchangeToJSON } from '../../../proto/generated/common'

interface ChartSettingsButtonsProps {
    allowSplit?: boolean,
    security: Security;
    viewKey: string;
}

const getFnoDedicatedPagePath = (route: string, security?: Security): string | undefined => {
    if (!security?.code) return undefined
    const code = isFnOSecurity(security) && security.underlyingSecurityCode
        ? security.underlyingSecurityCode
        : security.code
    if (!code) return undefined
    return generatePath(route, {
        exchange: exchangeToJSON(security.exchange),
        symbol: code,
    })
}

const getWidgetDedicatedPagePath = ({
    widgetType,
    settings,
    security,
}: {
    widgetType?: WidgetTypes
    settings?: { key?: string; expressionMode?: boolean }
    security?: Security
}): string | undefined => {
    const settingsKey = settings?.key
    const expressionMode = Boolean(settings?.expressionMode || (settingsKey && isExpressionMode(settingsKey)))

    if (widgetType === WidgetTypes.OPTION_CHAIN) {
        return getFnoDedicatedPagePath(ROUTES.optionChain, security)
    }
    if (widgetType === WidgetTypes.MULTI_STRIKE_CHART) {
        return getFnoDedicatedPagePath(ROUTES.multiStrikeChart, security)
    }
    if (widgetType === WidgetTypes.OPEN_INTEREST_CHART) {
        return getFnoDedicatedPagePath(ROUTES.openInterest, security)
    }
    if (widgetType === WidgetTypes.OI_CHART) {
        return getFnoDedicatedPagePath(ROUTES.openInterestChange, security)
    }

    if (expressionMode && settingsKey) {
        return generatePath(ROUTES.spread, { spread: settingsKey })
    }

    const symbolKey = settingsKey || (security ? getSecurityKey(security.exchange, security.code) : undefined)
    if (!symbolKey) return undefined
    if (isExpressionMode(symbolKey)) {
        return generatePath(ROUTES.spread, { spread: symbolKey })
    }
    return generatePath(ROUTES.symbol, { symbol: symbolKey })
}

/** Widgets whose x-axis is a chart time/logical scale (zoom, pan, reset). */
const TIME_NAVIGATION_WIDGET_TYPES: ReadonlySet<WidgetTypes> = new Set([
    WidgetTypes.CHART,
    WidgetTypes.FUTURE_CHART,
    WidgetTypes.OPTION_CHART,
    WidgetTypes.ATM_OPTION_CHART,
    WidgetTypes.MULTI_STRIKE_CHART,
    WidgetTypes.OI_CHART,
])

const CustomButton = styled(Button)(({ theme }) => {
    const hoverBg = alpha(theme.palette.mode === 'light' ? lightHoverBtnColor : darkHoverBtnColor, 0.8)
    return {
        maxHeight: '25px',
        minHeight: '25px',
        maxWidth: '25px',
        minWidth: '25px',
        padding: 0,
        margin: '2px',
        borderRadius: '2px',
        background: theme.palette.background.default,
        color: theme.palette.text.primary,
        pointerEvents: 'auto',
        cursor: 'pointer',
        '&:hover': {
            background: hoverBg,
            cursor: 'pointer',
        },
    }
})
const ChartSettingsButtons = ({ viewKey, security, allowSplit }: ChartSettingsButtonsProps) => {
    const { push } = useUndoRedoContext()
    const dispatch = useDispatch()
    const location = useLocation()
    const [open, setOpen] = useState(false)
    const appContext = useContext(AppContext)
    const activeLayoutId = useSelector(selectActiveLayoutId)
    const layout = useSelector(selectActiveLayout)
    const layoutRef = useRef<WidgetDashboardConfig>(null)
    const widgets = useSelector(selectLayoutWidgets(activeLayoutId))
    const activeWidget = useSelector(selectActiveWidget(activeLayoutId))
    const { key } = useSelector(selectPrimarySecurity)
    const [mainsecurity, setMainsecurity] = useState<Security | undefined>()
    useEffect(() => {
        if (!key) return
        getSecurityAsync(key).then(setMainsecurity)
    }, [key])
    const currentWidget = useMemo(() => {
        const dashIdx = viewKey.lastIndexOf('-')
        const slotId = dashIdx >= 0 ? Number(viewKey.slice(dashIdx + 1)) : NaN
        return Number.isFinite(slotId) ? widgets[slotId] : undefined
    }, [viewKey, widgets])
    const { user: { hasAccess } } = useUser()
    const multiStrikeChartType = useSelector(selectMultiStrikeChartConfigChartType(viewKey))
    const multiStrikeCumOi = useSelector(selectMultiStrikeChartConfigCumOi(viewKey))
    const multiStrikeShowChange = useSelector(selectMultiStrikeChartConfigShowChange(viewKey))
    const currentMultiStrikeMenuValue = useMemo(() => {
        if (currentWidget?.widgetType !== WidgetTypes.MULTI_STRIKE_CHART) return undefined
        return getMultiStrikeChartTypeMenuValue(multiStrikeChartType, multiStrikeCumOi, multiStrikeShowChange)
    }, [currentWidget?.widgetType, multiStrikeChartType, multiStrikeCumOi, multiStrikeShowChange])
    const [widgetType, setWidgetType] = useState<WidgetTypes>(currentWidget?.widgetType)
    useEffect(() => {
        if (currentWidget?.widgetType != null) {
            setWidgetType(currentWidget.widgetType)
        }
    }, [currentWidget?.widgetType])
    const resolvedWidgetType = currentWidget?.widgetType ?? widgetType
    const chart = appContext.getChart(viewKey)
    const [drawingInteractionActive, setDrawingInteractionActive] = useState(false)
    const chartApi: IChartApi = useMemo(() => {
        let chartApi = appContext.getChart(viewKey)?.chartApi
        if (widgetType === WidgetTypes.CHART) {
            chartApi = appContext.getChart(viewKey)?.chartApi
        } else if (widgetType === WidgetTypes.OI_CHART) {
            chartApi = appContext.getOiChart(viewKey)
        } else if (widgetType === WidgetTypes.MULTI_STRIKE_CHART) {
            chartApi = appContext.getMultiStrikeChart(viewKey)?.chartApi
        }
        return chartApi
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appContext, appContext.oiChart[viewKey], appContext.chart[viewKey], appContext.multiStrikeChart[viewKey], widgetType, viewKey])
    const btnRef = useRef<HTMLButtonElement | null>(null)
    const btnContainerRef = useRef<HTMLDivElement | null>(null)
    const drawingInteractionActiveRef = useRef(false)
    const paneTypeMenuOpenRef = useRef(false)
    const [paneTypeMenuOpen, setPaneTypeMenuOpen] = useState(false)
    const activeLayout = useSelector(selectLayout(activeLayoutId))
    const selectedPaneId = useSelector(selectWidgetPaneId)
    const isWidgetFullScreen = useSelector(FullScreenToggle)
    const timeScale = useMemo(() => chartApi?.timeScale(), [chartApi])
    const showTimeNavigationButtons = resolvedWidgetType
        ? TIME_NAVIGATION_WIDGET_TYPES.has(resolvedWidgetType)
        : Boolean(timeScale)
    const resolveChartElement = useCallback(() => {
        return (
            document.getElementById(`chart-${viewKey}`) ||
            document.getElementById(viewKey) ||
            btnContainerRef.current?.closest('.chart-selector') ||
            (btnContainerRef.current?.offsetParent as HTMLElement | null) ||
            null
        )
    }, [viewKey])

    const targetRef = useRef<HTMLElement | null>(resolveChartElement())

    useResizeDetector({ targetRef: targetRef as any })
    useEffect(() => {
        targetRef.current = resolveChartElement()
        // Chart mount node can appear shortly after buttons mount
        const timer = window.setTimeout(() => {
            targetRef.current = resolveChartElement()
        }, 0)
        return () => window.clearTimeout(timer)
    }, [resolveChartElement])

    useEffect(() => {
        layoutRef.current = layout
    }, [layout])

    useEffect(() => {
        paneTypeMenuOpenRef.current = paneTypeMenuOpen
    }, [paneTypeMenuOpen])

    useEffect(() => {
        drawingInteractionActiveRef.current = drawingInteractionActive
        const el = btnContainerRef.current
        if (!el) return
        if (drawingInteractionActive) {
            el.style.opacity = '0'
            el.style.visibility = 'hidden'
        } else {
            el.style.visibility = 'visible'
        }
    }, [drawingInteractionActive])

    useEffect(() => {
        // Drawing manager only exists on candle Chart instances
        if (widgetType && widgetType !== WidgetTypes.CHART) {
            setDrawingInteractionActive(false)
            return
        }

        let cancelled = false
        let chartInstance = appContext.getChart(viewKey)
        let retryTimer: ReturnType<typeof setTimeout> | undefined
        let attempts = 0

        const syncDrawingInteraction = () => {
            const dm = appContext.getChart(viewKey)?.drawingManager
            if (!dm) {
                setDrawingInteractionActive(false)
                return
            }
            setDrawingInteractionActive(Boolean(dm.getActiveDrawing() || dm.getFocusedDrawing()))
        }

        const onDrawingActive = () => setDrawingInteractionActive(true)
        const onDrawingInactive = () => syncDrawingInteraction()
        const onDrawingFocused = () => syncDrawingInteraction()
        const onDrawingDestroyed = () => syncDrawingInteraction()

        const unsubscribe = (instance: Chart | undefined) => {
            if (!instance) return
            instance.unsubscribeEvent(ChartEvents.DRAWING_ACTIVE, onDrawingActive)
            instance.unsubscribeEvent(ChartEvents.DRAWING_INACTIVE, onDrawingInactive)
            instance.unsubscribeEvent(ChartEvents.DRAWING_FOCUSED, onDrawingFocused)
            instance.unsubscribeEvent(ChartEvents.DRAWING_DESTROYED, onDrawingDestroyed)
        }

        const subscribe = (instance: Chart) => {
            instance.subscribeEvent(ChartEvents.DRAWING_ACTIVE, onDrawingActive)
            instance.subscribeEvent(ChartEvents.DRAWING_INACTIVE, onDrawingInactive)
            instance.subscribeEvent(ChartEvents.DRAWING_FOCUSED, onDrawingFocused)
            instance.subscribeEvent(ChartEvents.DRAWING_DESTROYED, onDrawingDestroyed)
            syncDrawingInteraction()
        }

        const trySubscribe = () => {
            if (cancelled) return
            chartInstance = appContext.getChart(viewKey)
            if (!chartInstance?.drawingManager) {
                attempts += 1
                if (attempts < 50) {
                    retryTimer = setTimeout(trySubscribe, 100)
                }
                return
            }
            subscribe(chartInstance)
        }

        trySubscribe()

        return () => {
            cancelled = true
            if (retryTimer) clearTimeout(retryTimer)
            unsubscribe(chartInstance)
        }
    }, [appContext, viewKey, widgetType])

    useEffect(() => {
        const HOVER_PADDING = 40
        const BOTTOM_BAND = 120
        const handleMouseMove = (e: MouseEvent) => {
            const el = btnContainerRef.current
            if (!el || drawingInteractionActiveRef.current) return

            if (paneTypeMenuOpenRef.current) {
                el.style.opacity = '1'
                return
            }

            // Scope hit-testing to this pane's chart so multipane slots don't share one target
            const chartEl = resolveChartElement()
            targetRef.current = chartEl
            const chartRect = chartEl?.getBoundingClientRect()
            const btnRect = el.getBoundingClientRect()

            const nearButtons =
                btnRect.width > 0 &&
                btnRect.height > 0 &&
                e.clientX >= btnRect.left - HOVER_PADDING &&
                e.clientX <= btnRect.right + HOVER_PADDING &&
                e.clientY >= btnRect.top - HOVER_PADDING &&
                e.clientY <= btnRect.bottom + HOVER_PADDING

            const inChartBottomBand =
                !!chartRect &&
                chartRect.width > 0 &&
                chartRect.height > 0 &&
                e.clientX >= chartRect.left &&
                e.clientX <= chartRect.right &&
                e.clientY >= chartRect.bottom - BOTTOM_BAND &&
                e.clientY <= chartRect.bottom

            el.style.opacity = nearButtons || inChartBottomBand ? '1' : '0'
        }

        window.addEventListener('mousemove', handleMouseMove)
        return () => {
            window.removeEventListener('mousemove', handleMouseMove)
        }
    }, [resolveChartElement])

    const showResetChartButton = useMemo(() => {
        try {
            if (widgetType === WidgetTypes.CHART && timeScale) {
                const currentVisibleLogicalRange = timeScale.getVisibleLogicalRange()
                const candles = (chart as Chart).getRenderedPrimaryData()
                const currentFrom = Math.max(candles.length - INITIAL_CANDLE_COUNT, 0)
                const currentTo = candles.length + 3
                const expectedFrom = currentVisibleLogicalRange?.from as number
                const expectedTo = currentVisibleLogicalRange?.to as number
                if ((currentFrom === expectedFrom && currentTo === expectedTo) && chartApi.priceScale('right').options().autoScale) {
                    return false
                }
                return true
            }
        } catch (e) {
            return true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chart])

    const resetTimeScale = useCallback(() => {
        AnalyticsManager.logEvent('Reset Chart Clicked', { source: 'chart-hovered-buttons' })
        appContext.getChart(viewKey).resetTimeScale()
    }, [appContext, viewKey])

    const splitHorizontal = useCallback(() => {
        currentWidget && appContext.dashboard?.splitPane(currentWidget.slotId, 2, true)
    }, [currentWidget, appContext])

    const splitVertical = useCallback(() => {
        currentWidget && appContext.dashboard?.splitPane(currentWidget.slotId, 2, false)
    }, [currentWidget, appContext])

    const visiblePaneCount = useMemo(() => countVisiblePanes(activeLayout?.panes), [activeLayout?.panes])
    const widgetCount = Object.keys(widgets || {}).length
    const canDeletePane = Boolean(currentWidget) && visiblePaneCount > 1 && widgetCount > 1

    const deleteCurrentWidget = useCallback(() => {
        if (!canDeletePane || !currentWidget) return
        AnalyticsManager.logEvent('Delete Widget Clicked', { source: 'chart-hovered-buttons' })
        appContext.dashboard?.deletePane(currentWidget.slotId)
        dispatch(deleteWidget({
            id: activeLayoutId,
            slotId: currentWidget.slotId
        }))
    }, [activeLayoutId, canDeletePane, currentWidget, appContext.dashboard, dispatch])

    const selectPaneType = useCallback(() => {
        setPaneTypeMenuOpen(s => !s)
    }, [])
    const isWidgetOptionSelected = useCallback((item: WidgetOptionMenuItem) => {
        if (currentWidget?.widgetType !== item.widgetType) return false
        if (!item.multiStrikeChartType) return true
        return currentMultiStrikeMenuValue === item.multiStrikeChartType
    }, [currentMultiStrikeMenuValue, currentWidget?.widgetType])

    const isWidgetOptionDisabled = useCallback((item: WidgetOptionMenuItem) => {
        return (item.widgetType !== WidgetTypes.CHART && !mainsecurity?.hasFo && [ SecurityCategory_CategoryCode.STOCKS, SecurityCategory_CategoryCode.INDEX ].includes(mainsecurity?.categoryCode))
            || (item.widgetType !== WidgetTypes.CHART && chart?.securities?.length > 1)
    }, [chart?.securities?.length, mainsecurity?.categoryCode, mainsecurity?.hasFo])

    const changeWidgetType = useCallback((item: WidgetOptionMenuItem) => {
        if (!currentWidget) return
        if (isWidgetOptionSelected(item)) {
            setPaneTypeMenuOpen(false)
            return
        }

        if (item.multiStrikeChartType) {
            const nextConfig = resolveMultiStrikeChartTypeSelection(
                item.multiStrikeChartType,
                multiStrikeCumOi,
                multiStrikeShowChange
            )
            if (isOiValueChartType(nextConfig.chartType) && !hasAccess(FeatureCategory.MULTI_STRIKE, FeatureSubCategory.OI_CHARTS)) {
                dispatch(setFeatureRestrictionDialog({
                    open: true,
                    feature: 'OI Charts',
                    featureRestrictionCode: 'MULTI_STRIKE_OI_CHARTS_MULTISTRIKE',
                    group: Group.MULTI_STRIKE
                }))
                setPaneTypeMenuOpen(false)
                return
            }
            dispatch(updateMultiStrikeChartConfig({
                viewKey,
                config: currentWidget.widgetType === WidgetTypes.MULTI_STRIKE_CHART
                    ? nextConfig
                    : getDefaultMultiStrikeChartSettings({
                        ...nextConfig,
                        key: currentWidget.settings?.key,
                    }),
            }))
        }

        if (currentWidget.widgetType !== item.widgetType) {
            const data = {
                chartId: activeLayoutId,
                slotId: currentWidget.slotId,
                previousType: currentWidget.widgetType,
                currentType: item.widgetType
            }
            push(data, `CHANGE_WIDGET_TYPE (${item.widgetType})`, ActionType.CHANGE_WIDGET_TYPE)
            setWidgetType(item.widgetType)
            const doUpdate = (settingsOverride?: { key: string; securities: string[]; underlyingKey?: string }) => {
                if (settingsOverride) {
                    dispatch(updateWidgetSettings({
                        id: activeLayoutId,
                        slotId: currentWidget.slotId,
                        settings: settingsOverride
                    }))
                }
                dispatch(updateWidgetType({
                    id: activeLayoutId,
                    slotId: currentWidget.slotId,
                    type: item.widgetType
                }))
            }
            const needsUnderlyingKey = (currentWidget.widgetType === WidgetTypes.OPTION_CHART || currentWidget.widgetType === WidgetTypes.ATM_OPTION_CHART) &&
                (item.widgetType === WidgetTypes.CHART || item.widgetType === WidgetTypes.FUTURE_CHART || item.widgetType === WidgetTypes.MULTI_STRIKE_CHART)
            const needsUnderlyingKeyForOption = (currentWidget.widgetType === WidgetTypes.CHART || currentWidget.widgetType === WidgetTypes.FUTURE_CHART) &&
                (item.widgetType === WidgetTypes.OPTION_CHART || item.widgetType === WidgetTypes.ATM_OPTION_CHART)
            if (needsUnderlyingKey) {
                const underlyingKey = currentWidget.settings?.underlyingKey
                if (underlyingKey) {
                    doUpdate({ key: underlyingKey, securities: [underlyingKey] })
                } else {
                    getSecurityAsync(currentWidget.settings?.key ?? '').then((security) => {
                        const key = security?.underlyingSecurityCode
                            ? getSecurityKey(security.exchange, security.underlyingSecurityCode)
                            : currentWidget.settings?.key
                        if (key) {
                            doUpdate({ key, securities: [key] })
                        } else {
                            doUpdate()
                        }
                    })
                }
            } else if (needsUnderlyingKeyForOption) {
                const underlyingKey = currentWidget.settings?.underlyingKey ?? currentWidget.settings?.key
                if (underlyingKey) {
                    doUpdate({ key: underlyingKey, securities: [underlyingKey], underlyingKey })
                } else {
                    doUpdate()
                }
            } else {
                doUpdate()
            }
        }
        AnalyticsManager.logEvent('Change Widget Type Clicked', {
            source: 'chart-hovered-buttons',
            widgetType: item.widgetType,
            multiStrikeChartType: item.multiStrikeChartType
        })
        setPaneTypeMenuOpen(false)
    }, [activeLayoutId, currentWidget, dispatch, hasAccess, isWidgetOptionSelected, multiStrikeCumOi, multiStrikeShowChange, push, viewKey])

    const renderWidgetOptionMenuItem = useCallback((item: WidgetOptionMenuItem) => (
        <MenuItem
            key={item.id}
            disabled={isWidgetOptionDisabled(item)}
            selected={isWidgetOptionSelected(item)}
            onClick={() => changeWidgetType(item)}
        >
            {item.label}
        </MenuItem>
    ), [changeWidgetType, isWidgetOptionDisabled, isWidgetOptionSelected])

    const handleCandleForwardBackward = useCallback((forward = true) => {
        if (timeScale) {
            const visibleRange = timeScale.getVisibleLogicalRange()
            if (visibleRange) {
                const newRange = {
                    from: forward ? visibleRange.from + 1 : visibleRange.from - 1,
                    to: forward ? visibleRange.to + 1 : visibleRange.to - 1
                }
                timeScale.setVisibleLogicalRange(newRange)
            }
            AnalyticsManager.logEvent(`Chart ${forward ? 'Forward' : 'Backward'} Clicked`, {
                source: 'chart-hovered-buttons'
            })
        }
    }, [timeScale])
    const handleZoomInOut = useCallback((zoomIn = true) => {
        if (timeScale) {
            AnalyticsManager.logEvent(`Chart Zoomed ${zoomIn ? 'In' : 'Out'} Clicked`, {
                source: 'chart-hovered-buttons'
            })
            const visibleRange = timeScale.getVisibleLogicalRange()
            const zoomWidth = 5.5
            if (visibleRange) {
                const from = zoomIn ? visibleRange.from + zoomWidth : visibleRange.from - zoomWidth
                const to = zoomIn ? visibleRange.to - zoomWidth : visibleRange.to + zoomWidth
                if (from < to) {
                    timeScale.setVisibleLogicalRange({ from, to })
                }
            }
        }
    }, [timeScale])
    const shortcuts: ShortcutType = useMemo(() => {
        const chartSettingsShortcuts: ShortcutType = {
            [ChartSettingsShortcutsKeyName.splitHorizontal]: (event) => {
                AnalyticsManager.logEvent('Chart Split Horizontally', { source: 'keyboard' })
                event.preventDefault()
                splitHorizontal()
            },
            [ChartSettingsShortcutsKeyName.splitVertical]: () => {
                AnalyticsManager.logEvent('Chart Split Vertically', { source: 'keyboard' })
                splitVertical()
            },
            [ChartSettingsShortcutsKeyName.deleteCurrentWidget]: () => {
                AnalyticsManager.logEvent('Chart Current Widget Deleted', { source: 'keyboard' })
                deleteCurrentWidget()
            }
        }
        if (showTimeNavigationButtons) {
            chartSettingsShortcuts[ChartSettingsShortcutsKeyName.zoomIn] = () => {
                AnalyticsManager.logEvent('Chart Zoomed In', { source: 'keyboard' })
                handleZoomInOut()
            }
            chartSettingsShortcuts[ChartSettingsShortcutsKeyName.zoomOut] = () => {
                AnalyticsManager.logEvent('Chart Zoomed Out', { source: 'keyboard' })
                handleZoomInOut(false)
            }
            chartSettingsShortcuts[ChartSettingsShortcutsKeyName.moveChartLeft] = () => {
                AnalyticsManager.logEvent('Chart Moved Left', { source: 'keyboard' })
                handleCandleForwardBackward(false)
            }
            chartSettingsShortcuts[ChartSettingsShortcutsKeyName.moveChartRight] = () => {
                AnalyticsManager.logEvent('Chart Moved Right', { source: 'keyboard' })
                handleCandleForwardBackward()
            }
        }
        return chartSettingsShortcuts
    }, [showTimeNavigationButtons, handleZoomInOut, handleCandleForwardBackward, splitHorizontal, splitVertical, deleteCurrentWidget])

    useShortcuts(ShortcutsCategories.CHART_SETTINGS, shortcuts, activeWidget?.slotId === currentWidget?.slotId)

    const handleFullScreenClick = useCallback(() => {
        AnalyticsManager.logEvent('Full Screen Clicked', {
            source: 'chart-hovered-buttons',
            fullScreen: !isWidgetFullScreen
        })
        const newPaneId = activeWidget?.slotId === selectedPaneId ? null : activeWidget?.slotId ?? 0
        dispatch(setWidgetsPaneId(newPaneId))
        dispatch(toggleFullscreen())
        setOpen(false)
    }, [activeWidget, selectedPaneId, dispatch])
    const container = useSelector(selectMultiPaneFullScreen)
    const handleSplitHorizontal = useCallback(() => {
        AnalyticsManager.logEvent('Split Layout Horizontal Clicked', { source: 'chart-hovered-buttons' })
        splitHorizontal()
        setTimeout(() => {
            push(layoutRef.current, 'Split Horizontal Layout', ActionType.UPDATE_LAYOUT)
        }, 1000)
    }, [])
    const handleSplitVertical = useCallback(() => {
        AnalyticsManager.logEvent('Split Layout Vertical Clicked', { source: 'chart-hovered-buttons' })
        splitVertical()
        setTimeout(() => {
            push(layoutRef.current, 'Split Vertical Layout', ActionType.UPDATE_LAYOUT)
        }, 1000)
    }, [])

    const dedicatedPagePath = useMemo(() => {
        if (isEmbeddedModeSet()) return undefined
        return getWidgetDedicatedPagePath({
            widgetType: resolvedWidgetType,
            settings: currentWidget?.settings,
            security,
        })
    }, [currentWidget?.settings, resolvedWidgetType, security])

    const isOnDedicatedPage = Boolean(
        dedicatedPagePath && decodeURIComponent(location.pathname) === decodeURIComponent(dedicatedPagePath)
    )

    const dedicatedPageTooltip = resolvedWidgetType === WidgetTypes.OPTION_CHAIN
        ? 'Open option chain'
        : resolvedWidgetType === WidgetTypes.MULTI_STRIKE_CHART
            ? 'Open multi strike'
            : resolvedWidgetType === WidgetTypes.OPEN_INTEREST_CHART
                ? 'Open interest'
                : resolvedWidgetType === WidgetTypes.OI_CHART
                    ? 'Open interest change'
                    : currentWidget?.settings?.expressionMode || (currentWidget?.settings?.key && isExpressionMode(currentWidget.settings.key))
                        ? 'Open spread'
                        : 'Open symbol'

    const handleOpenDedicatedPage = useCallback(() => {
        if (!dedicatedPagePath || isOnDedicatedPage) return
        AnalyticsManager.logEvent('Open Dedicated Page Clicked', {
            source: 'chart-hovered-buttons',
            widgetType: resolvedWidgetType,
        })
        window.open(dedicatedPagePath, '_blank')
    }, [dedicatedPagePath, isOnDedicatedPage, resolvedWidgetType])

    return (
        <Box
            ref={(ele: HTMLDivElement | null) => {
                btnContainerRef.current = ele
                if (ele) ele.classList.add('float-chart-settings-button')
            }}
            sx={{
                display: 'flex',
                position: 'absolute',
                bottom: '60px',
                left: '50%',
                zIndex: 99,
                opacity: paneTypeMenuOpen ? 1 : 0,
                visibility: drawingInteractionActive ? 'hidden' : 'visible',
                ':hover': {
                    opacity: drawingInteractionActive ? 0 : '1 !important'
                },
                transform: 'translateX(-50%)',
                pointerEvents: 'none'
            }}
        >
            <div style={{ marginRight: '5px', display: 'flex' }}>
                {canDeletePane && (
                    <Tooltip
                        title={!isWidgetFullScreen ? 'Maximize chart' : 'Restore chart'}
                        placement="top"
                        arrow
                        open={open}
                        onOpen={() => setOpen(true)}
                        onClose={() => setOpen(false)}
                        PopperProps={{
                            container: container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null
                        }}

                    >
                        <span
                            onMouseEnter={() => setOpen(true)}
                            onMouseLeave={() => setOpen(false)}
                        >
                            <CustomButton variant="contained" onClick={handleFullScreenClick}>
                                {!isWidgetFullScreen ? <FullScreenIcon /> : <FullScreenReverseIcon />}
                            </CustomButton>
                        </span></Tooltip>
                )}
                {!isWidgetFullScreen && allowSplit &&  (<>
                    <NonInteractiveTooltip placement={'top'} arrow title={'Split Horizontally'}
                        PopperProps={{ container: container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null }}>
                        <CustomButton variant="contained" onClick={handleSplitHorizontal}><SplitHorizontalIcon
                            fontSize="small" /></CustomButton>
                    </NonInteractiveTooltip>
                    <NonInteractiveTooltip placement={'top'} arrow title={'Split Vertically'}
                        PopperProps={{ container: container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null }}>
                        <CustomButton variant="contained" onClick={handleSplitVertical}><SplitVerticalIcon
                            fontSize="small" /></CustomButton>
                    </NonInteractiveTooltip>
                </>)}
                {canDeletePane && (
                    <NonInteractiveTooltip placement={'top'} arrow title={'Delete Pane'}
                        PopperProps={{ container: container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null }}>
                        <CustomButton variant="contained" onClick={deleteCurrentWidget}><BinIcon
                            fontSize={'small'} /></CustomButton>
                    </NonInteractiveTooltip>
                )}
                <NonInteractiveTooltip placement={'top'} arrow title={'Change Widget'}
                    PopperProps={{ container: container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null }}>
                    <CustomButton ref={btnRef} variant="contained" onClick={selectPaneType}><Widget
                        fontSize={'small'} /></CustomButton>
                </NonInteractiveTooltip>
                <Menu
                    anchorEl={btnRef.current}
                    open={paneTypeMenuOpen}
                    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    onClose={() => setPaneTypeMenuOpen(false)}
                >
                    {GLOBAL_WIDGET_OPTION_MENU_ITEMS.map(renderWidgetOptionMenuItem)}
                    <Divider
                        sx={{
                            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#434651' : '#e0e3eb'
                        }}
                    />
                    <ListSubheader
                        sx={{
                            lineHeight: '24px',
                            backgroundColor: 'unset',
                            fontSize: 11,
                            opacity: 0.5,
                            borderTop: 1,
                            px: '8px',
                            borderColor: (theme) => theme.palette.mode === 'dark' ? darkBg : white
                        }}
                    >
                        MULTI STRIKE
                    </ListSubheader>
                    {MULTI_STRIKE_WIDGET_OPTION_MENU_ITEMS.map(renderWidgetOptionMenuItem)}
                </Menu>
                {dedicatedPagePath && !isOnDedicatedPage && (
                    <NonInteractiveTooltip
                        placement={'top'}
                        arrow
                        title={dedicatedPageTooltip}
                        PopperProps={{ container: container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null }}
                    >
                        <CustomButton variant="contained" onClick={handleOpenDedicatedPage}>
                            <OpenInNew
                                fontSize="small"
                                sx={{
                                    stroke: (theme) => theme.palette.background.paper,
                                    strokeWidth: 0.8,
                                }}
                            />
                        </CustomButton>
                    </NonInteractiveTooltip>
                )}
            </div>
            {showTimeNavigationButtons && (
                <div style={{ marginRight: '5px', display: 'flex' }}>
                    <NonInteractiveTooltip placement={'top'} arrow title={'Zoom in'}
                        PopperProps={{ container: container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null }}>
                        <CustomButton variant="contained" onClick={() => handleZoomInOut()}><AddIcon
                            fontSize="small" /></CustomButton>
                    </NonInteractiveTooltip>
                    <NonInteractiveTooltip placement={'top'} arrow title={'Zoom out'}
                        PopperProps={{ container: container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null }}>
                        <CustomButton variant="contained" onClick={() => handleZoomInOut(false)}><SubtractIcon
                            fontSize="small" /></CustomButton>
                    </NonInteractiveTooltip>
                </div>
            )}
            {showTimeNavigationButtons && (
                <div style={{ marginRight: '5px', display: 'flex' }}>
                    <NonInteractiveTooltip placement={'top'} arrow title={'Scroll to the left'}
                        PopperProps={{ container: container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null }}>
                        <CustomButton variant="contained"
                            onClick={() => handleCandleForwardBackward(false)}><ThinArrowLeftIcon
                                fontSize="small" /></CustomButton>
                    </NonInteractiveTooltip>
                    <NonInteractiveTooltip placement={'top'} arrow title={'Scroll to the right'}
                        PopperProps={{ container: container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null }}>
                        <CustomButton variant="contained" onClick={() => handleCandleForwardBackward()}><ThinArrowRightIcon
                            fontSize="small" /></CustomButton>
                    </NonInteractiveTooltip>
                </div>
            )}
            {
                showTimeNavigationButtons && showResetChartButton &&
                <NonInteractiveTooltip placement={'top'} arrow title={'Reset chart view'}
                    PopperProps={{ container: container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null }}>
                    <CustomButton variant="contained" onClick={resetTimeScale}><ResetIcon
                        fontSize="small" /></CustomButton>
                </NonInteractiveTooltip>
            }
        </Box>
    )
}
export default memo(ChartSettingsButtons)